/**
 * Naming somebody in a message.
 *
 * A mention is stored as a row against a user id, not re-found in the text
 * later. The text is the display; the row is the fact. That distinction is
 * what makes a mention survive a rename, and what stops "Lee" matching inside
 * "@Lee Chen".
 *
 * The wire format is `@[Display Name](userId)`. Deliberately the same shape as
 * a Markdown link, so a client that has not been taught about mentions still
 * renders something a person can read rather than a raw id.
 */
/** `@[Someone](id)` — the id is a UUIDv7, so hyphens and hex only. */
const MENTION = /@\[([^\]]{1,120})\]\(([0-9a-fA-F-]{36})\)/g;
/**
 * Every mention in a message body, in the order they appear.
 *
 * Duplicates are kept: somebody named twice is two places in the text, even
 * though they are one row and one notification.
 */
export function parseMentions(body) {
    const found = [];
    // `matchAll` rather than a stateful `exec` loop: the regex is module-level
    // and `lastIndex` would leak between calls.
    for (const match of body.matchAll(MENTION)) {
        found.push({
            label: match[1],
            userId: match[2],
            start: match.index,
            end: match.index + match[0].length,
        });
    }
    return found;
}
/** The distinct people a message names. What gets written, and notified. */
export function mentionedUserIds(body) {
    return [...new Set(parseMentions(body).map((mention) => mention.userId))];
}
/**
 * The body as a person reads it, with the markup removed.
 *
 * For notification previews, search snippets and anywhere else the raw form
 * would leak an id into something somebody reads.
 */
export function stripMentions(body) {
    return body.replace(MENTION, (_match, label) => `@${label}`);
}
/** Build the wire form. One place, so the composer and tests cannot disagree. */
export function formatMention(userId, displayName) {
    return `@[${displayName}](${userId})`;
}
/**
 * Turn the readable `@Display Name` a person types into the wire form.
 *
 * The composer shows `@Junior`, not `@[Junior](019f…)` — a reference id in the
 * middle of a sentence is not something anybody wants to look at while typing.
 * So the readable text is what lives in the textarea, and this converts it to
 * the structural form once, at send, against the people who could actually be
 * named here (a channel's members).
 *
 * Longest name first, so `@Lee Chen` is matched whole before a bare `@Lee`
 * could claim the first word. Only a name followed by a boundary counts, so
 * `@Leena` is never read as `@Lee`. Anything already in wire form is left
 * alone, and an `@word` that matches nobody stays literal text.
 */
export function resolveMentions(text, people) {
    // Longest display name first: "Lee Chen" must win over "Lee".
    const ordered = [...people].sort((a, b) => b.displayName.length - a.displayName.length);
    let out = "";
    let i = 0;
    while (i < text.length) {
        if (text[i] !== "@") {
            out += text[i];
            i += 1;
            continue;
        }
        // Already a wire-form mention starting here? Copy it through untouched so a
        // second pass, or a paste of finished text, is not mangled.
        MENTION.lastIndex = i;
        const wire = MENTION.exec(text);
        if (wire && wire.index === i) {
            out += wire[0];
            i += wire[0].length;
            continue;
        }
        // A boundary before the "@" — start of text or whitespace — or it is an
        // email address, not a mention.
        const prev = i === 0 ? "" : text[i - 1];
        if (prev && !/\s/.test(prev)) {
            out += text[i];
            i += 1;
            continue;
        }
        const rest = text.slice(i + 1);
        const person = ordered.find((p) => {
            if (!rest.startsWith(p.displayName))
                return false;
            // The name must end at a word boundary, so "@Lee" does not swallow the
            // start of "Leena".
            const after = rest[p.displayName.length];
            return after === undefined || /[\s.,!?;:]/.test(after);
        });
        if (person) {
            out += formatMention(person.id, person.displayName);
            i += 1 + person.displayName.length;
        }
        else {
            out += text[i];
            i += 1;
        }
    }
    // A module-level regex with the global flag carries lastIndex between calls;
    // leaving it non-zero would make the next parseMentions miss the first match.
    MENTION.lastIndex = 0;
    return out;
}
//# sourceMappingURL=mentions.js.map